import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDialogElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {Set<HTMLElement>} Only nodes made inert by this drawer. */
  #isolated = new Set();
  /** @type {HTMLElement | null} */
  #activePanel = null;
  /** @type {HTMLElement | null} */
  #initialFocus = null;
  #localizationReturnKey = 'header-drawer:localization-return';

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#onKeyDown);
    this.refs.menuDrawer.addEventListener('cancel', this.#onCancel);
    this.refs.menuDrawer.addEventListener('click', this.#onBackdropClick);
    this.addEventListener('localization:change', this.#rememberLocalizationFocus);
    window.addEventListener('pageshow', this.#restoreLocalizationFocus);
    if (document.readyState === 'complete') this.#restoreLocalizationFocus();
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    removeTrapFocus(this);
    this.refs.menuDrawer.close();
    this.removeAttribute('data-localization-return');
    this.#restoreMenuLayers();
    this.removeEventListener('localization:change', this.#rememberLocalizationFocus);
    window.removeEventListener('pageshow', this.#restoreLocalizationFocus);
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.refs.menuDrawer.removeEventListener('cancel', this.#onCancel);
    this.refs.menuDrawer.removeEventListener('click', this.#onBackdropClick);
    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
    // Handle the active submenu first; suppress the dialog's default Escape close.
    event.preventDefault();
    const details = this.#getDetailsElement(event);
    this.#close(details.open ? details : this.refs.details);
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #onBackdropClick = (event) => {
    const panel = this.refs.menuDrawer;
    if (event.target === panel && isClickedOutside(event, panel)) this.close();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      if (!this.isConnected || !details.open) return;
      details.classList.add('menu-open');
      if (details === this.refs.details && !this.refs.menuDrawer.open) {
        const initialFocus = this.#initialFocus;
        const hadAutofocus = initialFocus?.hasAttribute('autofocus');
        if (initialFocus) {
          // A localization reload is a return to this control, not a new visit
          // to Close. Make it visible before native dialog autofocus runs.
          this.setAttribute('data-localization-return', '');
          initialFocus.setAttribute('autofocus', '');
        }
        // Native modality isolates the entire covered page, including touch
        // exploration and screen-reader navigation, not just the Tab sequence.
        this.refs.menuDrawer.showModal();
        if (initialFocus && !hadAutofocus) initialFocus.removeAttribute('autofocus');
      }
      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const panel = details === this.refs.details
        ? this.refs.menuDrawer
        : details.querySelector('.menu-drawer__submenu');
      if (panel instanceof HTMLElement) {
        this.#activateMenuLayer(panel, details === this.refs.details ? this.#initialFocus : null);
        this.#initialFocus = null;
      }
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer.
   * @param {{ immediate?: boolean }} [options]
   */
  close({ immediate = false } = {}) {
    if (immediate) {
      removeTrapFocus(this);
      this.refs.menuDrawer.close();
      this.removeAttribute('data-localization-return');
      this.#restoreMenuLayers();
      reset(this.refs.details);
      this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
      return;
    }
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (!this.isConnected || details.classList.contains('menu-open')) return;
        if (details === this.refs.details) {
          removeTrapFocus(this);
          // Keep the background inert throughout the closing animation.
          this.refs.menuDrawer.close();
          this.removeAttribute('data-localization-return');
          this.#restoreMenuLayers();
          reset(details);
          summary.focus({ preventScroll: true });
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          reset(details);
          if (this.refs.details.open && this.refs.details.classList.contains('menu-open')) {
            const parentPanel = details.parentElement?.closest('.menu-drawer__submenu') ?? this.refs.menuDrawer;
            this.#activateMenuLayer(parentPanel, summary);
          }
        }
      },
      { subtree: false }
    );
  }

  /** Restore only inert states owned by this component. */
  #restoreMenuLayers() {
    for (const element of this.#isolated) element.inert = false;
    this.#isolated.clear();
    this.#activePanel = null;
  }

  /**
   * Isolate sibling branches up to the modal, never an ancestor of the active panel.
   * @param {HTMLElement} panel
   * @param {Element | null} [returnTarget]
   */
  #activateMenuLayer(panel, returnTarget) {
    removeTrapFocus(this);
    this.#restoreMenuLayers();
    this.#activePanel = panel;
    let branch = panel;
    while (branch !== this.refs.menuDrawer && branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch && sibling instanceof HTMLElement && !sibling.inert) {
          sibling.inert = true;
          this.#isolated.add(sibling);
        }
      }
      branch = branch.parentElement;
    }
    const target = returnTarget ?? panel.querySelector('.menu-drawer__back-button, .menu-drawer__close-button');
    let focusPlaced = false;
    const focusPanel = (preserveFocus = true) => {
      if (!this.isConnected || !this.isOpen || this.#activePanel !== panel ||
          !this.refs.details.classList.contains('menu-open')) return;
      const focused = document.activeElement;
      trapFocus(panel, { focusContainer: false });
      const destination = preserveFocus && focusPlaced && focused instanceof HTMLElement && focused !== panel && panel.contains(focused)
        ? focused : target;
      if (destination instanceof HTMLElement) {
        if (document.activeElement !== destination) destination.focus({ preventScroll: true });
        focusPlaced = document.activeElement === destination;
      }
    };
    focusPanel(false);
    // Sliding panels can still be visibility:hidden in their first frame.
    // Only the currently active layer may finish the focus transition.
    // The utility row has a staggered animation longer than the panel itself.
    const animatedTarget = target?.closest('.menu-drawer__animated-element');
    const animatedElements = animatedTarget && panel.contains(animatedTarget) ? [panel, animatedTarget] : panel;
    onAnimationEnd(animatedElements, focusPanel, { subtree: false });
  }

  /** Match the same page across Shopify locale prefixes, e.g. / and /en-ca. */
  #localizationPath() {
    const prefix = (this.dataset.localizationRoot ?? '/').replace(/\/$/, '');
    const path = location.pathname;
    return prefix && (path === prefix || path.startsWith(`${prefix}/`))
      ? path.slice(prefix.length) || '/' : path;
  }

  #rememberLocalizationFocus = () => {
    if (!this.isOpen) return;
    try {
      sessionStorage.setItem(this.#localizationReturnKey, JSON.stringify({ path: this.#localizationPath(), at: Date.now() }));
    } catch {
      // Localization must still submit when browser storage is unavailable.
    }
  };

  #restoreLocalizationFocus = () => {
    let saved;
    try {
      const value = sessionStorage.getItem(this.#localizationReturnKey);
      if (!value) return;
      sessionStorage.removeItem(this.#localizationReturnKey);
      saved = JSON.parse(value);
    } catch {
      return;
    }
    if (!saved || saved.path !== this.#localizationPath() || !Number.isFinite(saved.at) ||
        Date.now() - saved.at < 0 || Date.now() - saved.at > 60000) return;
    const trigger = this.refs.details.querySelector('summary');
    const region = this.querySelector('#HeaderDrawer-localization');
    if (!(region instanceof HTMLElement) || !trigger?.getClientRects().length) return;
    this.#initialFocus = region;
    this.refs.details.open = true;
    this.open();
  };

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
